import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Send, Trash2, PlusCircle, Paperclip } from "lucide-react";
import Layout from "@/components/Layout";
import EmergencyToggle from "@/components/EmergencyToggle";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  Section, Field, TopNotice,
  ApplicantSpocSection, SelfSkipSection,
  isHodDropdownOptional, stripSelfSkipTransients, ResubmitFromSection,
} from "@/components/FormShell";
import {
  Select, SelectTrigger, SelectValue,
  SelectContent, SelectItem,
} from "@/components/ui/select";

// Phase 21 (item 1) — DD/MM/YYYY mask helper (mirrors TrackerGrid.jsx so
// the placeholder is honoured natively and auto-slash feels natural).
function _maskDdmmyyyy(raw) {
  const d = (raw || "").replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}
function _ddmmyyyyToIso(s) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((s || "").trim());
  if (!m) return "";
  const [, dd, mm, yyyy] = m;
  const d = Number(dd), mo = Number(mm);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return "";
  return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
}

// Phase 20 — Rate card must mirror backend workflow.PRINT_JOB_RATE_CARD
// exactly. Server recomputes and rejects on > ₹0.01 drift.
const RATE = {
  "Black & White|One Side":   0.50,
  "Black & White|Both Sides": 1.00,
  "Color|One Side":           10.00,
  "Color|Both Sides":         20.00,
};
const PAPER_SIZES = ["A4", "A3"];
const COLORS = ["Black & White", "Color"];
const SIDES = ["One Side", "Both Sides"];
const PAPER_TYPES = ["Normal", "Bond", "Photo paper (A4)"];
const ORIENTATIONS = ["Portrait", "Landscape"];

// Phase 21.2 (item 3, follow-up) — Print-Job-specific Requested-By section
// using shadcn Select (button-based combobox) because native <select>
// ignores `line-height` in Chromium, blocking the ≥22.4px line-height
// readability requirement. Shadcn trigger is a <button> which honors it.
function PrintJobRequestedBySection({ form, setField, depts, hods, subDepts,
                                       hodOptional }) {
  const triggerCls =
    "pj-combobox-trigger w-full flex items-center justify-between " +
    "rounded-md border border-[#e2d7c3] bg-white text-[#1f2320] " +
    "data-[placeholder]:text-[#94918a] focus:outline-none " +
    "focus:border-[#7a1f2b] focus:ring-2 focus:ring-[#7a1f2b]/20 " +
    "transition-colors";
  const triggerStyle = { fontSize: "16px", lineHeight: "24px",
                          minHeight: "44px", padding: "10px 12px" };
  const itemCls =
    "pj-combobox-item cursor-pointer rounded-sm select-none " +
    "focus:bg-[#f4e9eb] focus:text-[#7a1f2b] data-[highlighted]:bg-[#f4e9eb]";
  const itemStyle = { fontSize: "16px", lineHeight: "24px",
                       padding: "8px 10px", minHeight: "40px" };

  return (
    <Section numeral="II" title="Requested by">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Main department" required>
          <Select value={form.mainDept || ""}
                  onValueChange={(v) => { setField("mainDept", v);
                                           setField("subDept", ""); }}>
            <SelectTrigger className={triggerCls} style={triggerStyle}
                           data-testid="field-mainDept">
              <SelectValue placeholder="Select department" />
            </SelectTrigger>
            <SelectContent className="pj-combobox-content">
              {depts.map((d) => (
                <SelectItem key={d.id} value={d.name}
                            className={itemCls} style={itemStyle}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Sub department" required>
          <Select value={form.subDept || ""}
                  onValueChange={(v) => setField("subDept", v)}
                  disabled={!subDepts.length}>
            <SelectTrigger className={triggerCls} style={triggerStyle}
                           data-testid="field-subDept">
              <SelectValue placeholder={subDepts.length
                ? "Select sub-department"
                : "Select main department first"} />
            </SelectTrigger>
            <SelectContent className="pj-combobox-content">
              {subDepts.map((s) => (
                <SelectItem key={s} value={s}
                            className={itemCls} style={itemStyle}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="Select the respective HOD for consent & approval"
             required={!hodOptional}>
        <Select value={form.hodApproval || ""}
                onValueChange={(v) => setField("hodApproval", v)}>
          <SelectTrigger className={triggerCls} style={triggerStyle}
                         data-testid="field-hodApproval">
            <SelectValue placeholder={hodOptional
              ? "Optional — HOD will be skipped"
              : "Select HOD"} />
          </SelectTrigger>
          <SelectContent className="pj-combobox-content">
            {hods.map((h) => (
              <SelectItem key={h.email} value={h.email}
                          className={itemCls} style={itemStyle}>
                {((h.departments || []).join(", ") || "—") + " — " + h.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hodOptional ? (
          <div className="mt-1 text-[11px] leading-snug text-[#2d6b6b]"
               data-testid="hod-self-skip-hint">
            HOD approval will be skipped based on your self-declaration.
          </div>
        ) : (
          <div className="mt-1 text-[11px] text-red-600 leading-snug">
            If the form is Material, Service, Medical, or Safety-Security,
            then HOD can select the HOD of the respective department.
          </div>
        )}
      </Field>
    </Section>
  );
}

const emptyRow = () => ({
  file_name: "", paper_size: "A4", paper_size_other: "",
  pages: 1, copies: 1, color: "Black & White", side: "One Side",
  paper_type: "Normal", orientation: "Portrait", remark: "",
  attachment: null,
});

const rowCost = (r) => {
  const key = `${r.color}|${r.side}`;
  const rate = RATE[key] || 0;
  const p = Number(r.pages) || 0;
  const n = Number(r.copies) || 0;
  return Math.round(rate * Math.max(0, p) * Math.max(0, n) * 100) / 100;
};

const inr = (n) => `₹${(Number(n) || 0).toFixed(2)}`;

export default function NewPrintJob() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [depts, setDepts] = useState([]);
  const [hods, setHods] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingIx, setUploadingIx] = useState(null);
  const [isResubmit, setIsResubmit] = useState(false);
  const [parentRefCode, setParentRefCode] = useState(null);
  // Phase 21.4 Fix C — PO health gate (fired once on mount).
  const [poHealth, setPoHealth] = useState(null);  // {ok, can_seed, email}
  const [seedingPO, setSeedingPO] = useState(false);

  const [form, setForm] = useState({
    staffName: user?.name || "",
    staffEmail: user?.email || "",
    staffContact: "",
    mainDept: "", subDept: "",
    hodApproval: "",
    is_hod_self: undefined,
    // Phase 21 (item 2) — Admin-1 toggle removed; Print Job has no Admin-1
    // stage. HOD self-skip remains.
    purposeOfPrint: "",
    tentative_required_by: "",  // DD/MM/YYYY (masked); server stores ISO
    tentative_required_by_time: "",  // HH:MM 24h (server stores)
    consent_quality_disclaimer: false,  // Phase 21.2 (item 2)
    print_rows: [emptyRow()],
    is_emergency: false,
    emergency_reason: "",
  });

  useEffect(() => {
    api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
    api.get("/hods").then((r) => setHods(r.data)).catch(() => {});
    // Phase 21.4 Fix C — Preflight the Print Job PO user health. One shot
    // per mount; the inline warning + seed button react to this state.
    api.get("/admin/print-job-po-health")
      .then((r) => setPoHealth(r.data))
      .catch(() => setPoHealth({ ok: false, can_seed: false,
                                   email: "print.job@mitgurukul.com" }));
  }, []);

  const subDepts = useMemo(() => {
    const d = depts.find((x) => x.name === form.mainDept);
    return d?.sub_departments || [];
  }, [depts, form.mainDept]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Phase 21.4 Fix C — Super-admin one-click PO seed.
  const seedPrintJobPO = async () => {
    setSeedingPO(true);
    try {
      const { data } = await api.post("/admin/seed-print-job-po");
      toast.success(data.created
        ? `Print Job PO seeded (${data.email}).`
        : `Print Job PO already present.`);
      // Re-poll the health so the warning disappears.
      const h = await api.get("/admin/print-job-po-health");
      setPoHealth(h.data);
    } catch (e) {
      toast.error(formatApiError(e) || "Seed failed.");
    } finally {
      setSeedingPO(false);
    }
  };

  const setRow = (i, k, v) =>
    setForm((f) => {
      const rows = [...f.print_rows];
      rows[i] = { ...rows[i], [k]: v };
      return { ...f, print_rows: rows };
    });

  const addRow = () =>
    setForm((f) => ({ ...f, print_rows: [...f.print_rows, emptyRow()] }));

  const removeRow = (i) =>
    setForm((f) => {
      if (f.print_rows.length <= 1) {
        toast.error("At least one row is required.");
        return f;
      }
      const rows = f.print_rows.filter((_, ix) => ix !== i);
      return { ...f, print_rows: rows };
    });

  const uploadFor = async (i, file) => {
    if (!file) return;
    if (file.type && file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed per row.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("PDF must be ≤ 10 MB.");
      return;
    }
    setUploadingIx(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setRow(i, "attachment", {
        filename: data.filename, url: data.url, size: data.size,
        mime: "application/pdf",
      });
      toast.success("PDF attached");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadingIx(null);
    }
  };

  const grandTotal = useMemo(
    () => Math.round(form.print_rows.reduce((s, r) => s + rowCost(r), 0) * 100) / 100,
    [form.print_rows],
  );

  // Phase 21.1 — live aggregate copies/pages for display, daily 750-page cap.
  const totalCopies = useMemo(
    () => form.print_rows.reduce(
      (s, r) => s + Math.max(0, Number(r.copies) || 0), 0),
    [form.print_rows],
  );
  const totalPages = useMemo(
    () => form.print_rows.reduce(
      (s, r) => s + Math.max(0, Number(r.pages) || 0)
                   * Math.max(0, Number(r.copies) || 0), 0),
    [form.print_rows],
  );
  const overLimit = totalPages > 750;

  const validate = () => {
    const req = ["staffName", "staffEmail", "staffContact",
                 "mainDept", "subDept", "purposeOfPrint"];
    for (const k of req) {
      if (!String(form[k] || "").trim()) {
        toast.error(`Please fill: ${k}`); return false;
      }
    }
    if (!(form.is_hod_self === true)
        && !String(form.hodApproval || "").trim()) {
      toast.error("Please select an HOD or declare Yes on Are you a HOD?");
      return false;
    }
    for (let i = 0; i < form.print_rows.length; i++) {
      const r = form.print_rows[i];
      if (!r.file_name.trim()) {
        toast.error(`Row ${i + 1}: File Name is required.`); return false;
      }
      if (!(Number(r.pages) >= 1)) {
        toast.error(`Row ${i + 1}: Total Number of Pages must be ≥ 1.`); return false;
      }
      if (!(Number(r.copies) >= 1)) {
        toast.error(`Row ${i + 1}: Number of Copies must be ≥ 1.`); return false;
      }
    }
    // Phase 21 (item 1) — Tentative date required by: DD/MM/YYYY, not past.
    const trbIso = _ddmmyyyyToIso(form.tentative_required_by);
    if (!trbIso) {
      toast.error("Please enter a valid Tentative date (DD/MM/YYYY).");
      return false;
    }
    const todayIso = new Date().toISOString().slice(0, 10);
    if (trbIso < todayIso) {
      toast.error("Tentative date cannot be in the past.");
      return false;
    }
    // Phase 21.2 (item 1) — Required-by time (HH:MM 24h).
    if (!/^\d{2}:\d{2}$/.test(form.tentative_required_by_time || "")) {
      toast.error("Please select a Required-by time (HH:MM AM/PM).");
      return false;
    }
    if (trbIso === todayIso) {
      const [hh, mm] = form.tentative_required_by_time.split(":").map(Number);
      const now = new Date();
      const deadline = new Date(now);
      deadline.setHours(hh, mm, 0, 0);
      if (deadline <= now) {
        toast.error("Required-by date & time cannot be in the past.");
        return false;
      }
    }
    // Phase 21.2 (item 2) — consent checkbox mandatory.
    if (form.consent_quality_disclaimer !== true) {
      toast.error("Please acknowledge the Print Team disclaimer.");
      return false;
    }
    if (form.is_emergency && (form.emergency_reason || "").trim().length < 10) {
      toast.error("Emergency Reason is mandatory (min 10 characters).");
      return false;
    }
    // Phase 21.1 — enforce daily printing limit (authoritative check is backend).
    if (totalPages > 750) {
      toast.error(
        `Total pages (${totalPages}) exceeds the daily printing limit of 750. `
        + `Please reduce copies or split into multiple requisitions.`);
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const rows = form.print_rows.map((r, i) => ({
        ...r, sl_no: i + 1,
        pages: Number(r.pages), copies: Number(r.copies),
        row_cost: rowCost(r),
      }));
      const form_data = stripSelfSkipTransients({
        ...form, print_rows: rows, grand_total: grandTotal,
        // Phase 21 (item 1) — Convert DD/MM/YYYY → ISO before send.
        tentative_required_by: _ddmmyyyyToIso(form.tentative_required_by),
      });
      // Phase 21 (item 2) — Admin-1 self-skip not applicable to Print Job.
      delete form_data.is_admin1_self;
      delete form_data.admin1_self_cost;
      delete form_data.admin1_self_remark;
      delete form_data.skip_admin1_claimed;
      const payload = { form_type: "print_job",
                        parent_ref_code: parentRefCode || undefined,
                        form_data };
      const { data } = await api.post("/requisitions/print_job", payload);
      toast.success(`Submitted · ${data.ref_code}`, { duration: 5000 });
      nav(`/requisitions/${data.ref_code}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const hodOptional = isHodDropdownOptional(form);

  return (
    <Layout>
      <Toaster position="top-right" />
      <div className="max-w-6xl mx-auto relative print-job-form-scope">
        {/* Phase 21.3 Fix 1 — Dedicated header with logo on the right to
            prevent the absolute FormLogo from overlapping the red note
            block. On Print Job, the red note sits very near the top of
            the form so an in-flow header is required. */}
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">
              Print Job Requisition
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl text-[#1f2320] mt-1 leading-tight">
              Request a print job
            </h1>
          </div>
          <img src="/vgws-logo.png" alt="VGWS"
               className="shrink-0 h-10 sm:h-14 w-auto opacity-95"
               data-testid="print-job-vgws-logo" />
        </div>

        {/* Phase 21.4 Fix C — Inline, dedup'd PO-health warning. Shown
            only when the PO user is missing. Super-admins get a seed
            button; everyone else sees a friendly message. */}
        {poHealth && !poHealth.ok && (
          <div className="mb-4 rounded-md border-l-4 border-[#b3261e]
                           bg-red-50 text-[#1f2320] px-4 py-3 flex items-start
                           justify-between gap-3"
               role="alert" data-testid="print-job-po-missing-banner">
            <div className="text-sm leading-snug">
              {poHealth.can_seed ? (
                <>
                  <div className="font-semibold text-[#b3261e]">
                    Print Job Process Owner is not configured.
                  </div>
                  <div className="mt-1 text-[13px]">
                    Click <strong>Seed Print Job PO</strong> to create the
                    user <code>{poHealth.email}</code>. This is idempotent
                    and safe.
                  </div>
                </>
              ) : (
                <>
                  <div className="font-semibold text-[#b3261e]">
                    The Print Job Process Owner is not configured yet.
                  </div>
                  <div className="mt-1 text-[13px]">
                    Please contact your administrator before submitting.
                  </div>
                </>
              )}
            </div>
            {poHealth.can_seed && (
              <button type="button"
                      className="btn-vgws btn-maroon shrink-0"
                      onClick={seedPrintJobPO}
                      disabled={seedingPO}
                      data-testid="print-job-seed-po-btn">
                {seedingPO ? "Seeding…" : "Seed Print Job PO"}
              </button>
            )}
          </div>
        )}

        <TopNotice>
          Submit print jobs for class handouts, brochures, reports or event
          collateral. HOD approves first; the Print Job Process Owner then
          fulfils and marks delivery. HOD may update the decision multiple
          times until the Print PO marks fulfilment.
        </TopNotice>

        {/* Phase 21 (item 3) — Important timeline notice (static). */}
        <div className="mt-4 border-l-4 border-red-500 bg-red-50 text-red-700 rounded px-4 py-3 space-y-1 text-sm font-semibold uppercase tracking-wide"
             data-testid="print-job-timeline-notice" role="note" aria-live="polite">
          <div>⚠ The ideal timeline to submit print job requisition is 1 working day prior 2PM of working hours in a day to fulfill the requirement.</div>
          <div>⚠ Print job Requisition Process works on the basis of First Come First Served.</div>
          <div>⚠ Print job Requisition form submitted on Sundays or non-working or declared holidays will be considered for next working day.</div>
          <div>⚠ The daily printing limit is 750 pages — forms exceeding this limit cannot be submitted.</div>
        </div>

        <EmergencyToggle
          isEmergency={form.is_emergency}
          onToggle={(v) => setForm((f) => ({
            ...f, is_emergency: v,
            emergency_reason: v ? f.emergency_reason : "",
          }))}
          emergencyReason={form.emergency_reason}
          onReasonChange={(v) => setField("emergency_reason", v)}
        />

        <ResubmitFromSection
          isResubmit={isResubmit}
          onToggle={(v) => { setIsResubmit(v); if (!v) setParentRefCode(null); }}
          parentRefCode={parentRefCode}
          onSelectHistory={(fd, ref) => {
            setForm((f) => ({ ...f, ...fd }));
            setParentRefCode(ref);
          }}
        />

        <ApplicantSpocSection form={form} setField={setField} />

        <PrintJobRequestedBySection
          form={form} setField={setField}
          depts={depts} hods={hods} subDepts={subDepts}
          hodOptional={hodOptional}
        />

        <SelfSkipSection form={form} setField={setField}
                         userRole={user?.role} hideAdmin1 />

        <Section numeral="III" title="Purpose of print">
          <Field label="Purpose of Print" required>
            <textarea className="input-vgws" rows={3}
                      value={form.purposeOfPrint}
                      onChange={(e) => setField("purposeOfPrint", e.target.value)}
                      placeholder="e.g. Grade 5 PYP class handouts for the week"
                      data-testid="print-job-purpose" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Tentative date material required by" required
                   hint="Format: DD/MM/YYYY. Must be today or later.">
              <input type="text" inputMode="numeric" maxLength={10}
                     className="input-vgws text-sm placeholder:text-[#c7bda8] placeholder:italic"
                     value={form.tentative_required_by}
                     onChange={(e) => setField("tentative_required_by", _maskDdmmyyyy(e.target.value))}
                     placeholder="DD/MM/YYYY"
                     data-testid="print-job-tentative-required-by" />
            </Field>
            <Field label="Required-by time" required
                   hint="Format: 00:00 AM/PM">
              <input type="time"
                     className="input-vgws text-base"
                     style={{ fontSize: "16px", lineHeight: 1.4 }}
                     value={form.tentative_required_by_time}
                     onChange={(e) => setField("tentative_required_by_time", e.target.value)}
                     data-testid="print-job-tentative-required-by-time" />
            </Field>
          </div>
        </Section>

        <Section numeral="IV" title="Print rows">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="print-rows-table">
              <thead className="bg-[#7a1f2b] text-white">
                <tr>
                  <th className="px-2 py-2 text-left text-[11px] uppercase tracking-wider">#</th>
                  <th className="px-2 py-2 text-left text-[11px] uppercase tracking-wider">File Name</th>
                  <th className="px-2 py-2 text-left text-[11px] uppercase tracking-wider">PDF (≤10MB)</th>
                  <th className="px-2 py-2 text-left text-[11px] uppercase tracking-wider"
                      title="Total Number of Pages of Doc">Total Pages</th>
                  <th className="px-2 py-2 text-left text-[11px] uppercase tracking-wider">Paper Size</th>
                  <th className="px-2 py-2 text-left text-[11px] uppercase tracking-wider">Copies</th>
                  <th className="px-2 py-2 text-left text-[11px] uppercase tracking-wider">Color</th>
                  <th className="px-2 py-2 text-left text-[11px] uppercase tracking-wider">Side</th>
                  <th className="px-2 py-2 text-left text-[11px] uppercase tracking-wider">Paper Type</th>
                  <th className="px-2 py-2 text-left text-[11px] uppercase tracking-wider">Orientation</th>
                  <th className="px-2 py-2 text-left text-[11px] uppercase tracking-wider">Remark</th>
                  <th className="px-2 py-2 text-right text-[11px] uppercase tracking-wider">Cost</th>
                  <th className="px-2 py-2 text-center text-[11px] uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody>
                {form.print_rows.map((r, i) => (
                  <tr key={i} className="odd:bg-white even:bg-[#faf5eb] align-top">
                    <td className="px-2 py-2 text-xs text-[#6e7770]">{i + 1}</td>
                    <td className="px-2 py-2">
                      <input className="input-vgws text-sm"
                             value={r.file_name}
                             onChange={(e) => setRow(i, "file_name", e.target.value)}
                             placeholder="e.g. syllabus.pdf"
                             data-testid={`print-row-filename-${i}`} />
                    </td>
                    <td className="px-2 py-2">
                      <label className="inline-flex items-center gap-1 px-2 py-1 border border-dashed border-[#cbbba0] rounded-md bg-white cursor-pointer hover:border-[#7a1f2b] transition-colors"
                             data-testid={`print-row-upload-${i}`}>
                        <Paperclip size={14} className="text-[#7a1f2b]" />
                        <span className="text-xs text-[#4a524c] max-w-[120px] truncate">
                          {r.attachment ? r.attachment.filename
                            : uploadingIx === i ? "Uploading…" : "PDF"}
                        </span>
                        <input type="file" accept="application/pdf"
                               onChange={(e) => uploadFor(i, e.target.files?.[0])}
                               className="hidden" />
                      </label>
                    </td>
                    <td className="px-2 py-2">
                      <input type="number" min="1" step="1"
                             className="input-vgws text-sm w-20"
                             value={r.pages}
                             onChange={(e) => setRow(i, "pages", e.target.value)}
                             title="Total Number of Pages of Doc"
                             data-testid={`print-row-pages-${i}`} />
                    </td>
                    <td className="px-2 py-2">
                      <select className="input-vgws text-sm pj-table-select"
                              value={r.paper_size}
                              onChange={(e) => setRow(i, "paper_size", e.target.value)}
                              title={`Paper size: ${r.paper_size}`}
                              data-testid={`print-row-size-${i}`}>
                        {PAPER_SIZES.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-2">
                      <input type="number" min="1" step="1"
                             className="input-vgws text-sm w-20"
                             value={r.copies}
                             onChange={(e) => setRow(i, "copies", e.target.value)}
                             data-testid={`print-row-copies-${i}`} />
                    </td>
                    <td className="px-2 py-2">
                      <select className="input-vgws text-sm pj-table-select"
                              value={r.color}
                              onChange={(e) => setRow(i, "color", e.target.value)}
                              title={`Color: ${r.color}`}
                              data-testid={`print-row-color-${i}`}>
                        {COLORS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-2">
                      <select className="input-vgws text-sm pj-table-select"
                              value={r.side}
                              onChange={(e) => setRow(i, "side", e.target.value)}
                              title={`Side: ${r.side}`}
                              data-testid={`print-row-side-${i}`}>
                        {SIDES.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-2">
                      <select className="input-vgws text-sm pj-table-select"
                              value={r.paper_type}
                              onChange={(e) => setRow(i, "paper_type", e.target.value)}
                              title={`Paper type: ${r.paper_type}`}>
                        {PAPER_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-2">
                      <select className="input-vgws text-sm pj-table-select"
                              value={r.orientation}
                              onChange={(e) => setRow(i, "orientation", e.target.value)}
                              title={`Orientation: ${r.orientation}`}>
                        {ORIENTATIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-2">
                      <input className="input-vgws text-sm"
                             value={r.remark}
                             onChange={(e) => setRow(i, "remark", e.target.value)}
                             placeholder="optional" />
                    </td>
                    <td className="px-2 py-2 text-right font-semibold text-[#1f2320] tabular-nums"
                        data-testid={`print-row-cost-${i}`}>
                      {inr(rowCost(r))}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button type="button" className="text-[#b3261e] hover:text-[#7a1f2b]"
                              onClick={() => removeRow(i)} aria-label={`Remove row ${i + 1}`}
                              data-testid={`print-row-remove-${i}`}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                <tr className="bg-[#faf5eb] font-semibold border-t-2 border-[#7a1f2b]">
                  <td colSpan={11} className="px-2 py-2 text-right text-sm">
                    Grand Total
                  </td>
                  <td className="px-2 py-2 text-right text-sm text-[#7a1f2b] tabular-nums"
                      data-testid="print-grand-total">
                    {inr(grandTotal)}
                  </td>
                  <td></td>
                </tr>
                {/* Phase 21.1 — Total Copies */}
                <tr className="bg-[#faf5eb] font-semibold">
                  <td colSpan={11} className="px-2 py-2 text-right text-sm">
                    Total Copies
                  </td>
                  <td className="px-2 py-2 text-right text-sm text-[#1f2320] tabular-nums"
                      data-testid="print-total-copies">
                    {totalCopies}
                  </td>
                  <td></td>
                </tr>
                {/* Phase 21.1 — Total Pages with 750-page cap signal */}
                <tr className="bg-[#faf5eb] font-semibold">
                  <td colSpan={11} className="px-2 py-2 text-right text-sm">
                    Total Pages <span className="text-[11px] font-normal text-[#6e7770]">(limit 750 / day)</span>
                  </td>
                  <td className={`px-2 py-2 text-right text-sm tabular-nums ${
                        overLimit ? "text-[#b3261e] font-bold" : "text-[#1f2320]"
                      }`}
                      data-testid="print-total-pages">
                    {totalPages}
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
          {overLimit && (
            <div className="mt-2 border-l-4 border-[#b3261e] bg-red-50 text-[#b3261e] rounded px-3 py-2 text-sm font-semibold"
                 role="alert" data-testid="print-over-limit-error">
              Total pages ({totalPages}) exceeds the daily printing limit of 750.
              Please reduce copies or split into multiple requisitions.
            </div>
          )}
          <div className="mt-3 flex items-center gap-3">
            <button type="button" className="btn-ghost" onClick={addRow}
                    data-testid="print-add-row">
              <PlusCircle size={16} /> Add row
            </button>
            <span className="text-xs text-[#6e7770]">
              {form.print_rows.length} row(s). Server recomputes every cost; mismatches &gt; ₹0.01 are rejected.
            </span>
          </div>
          <div className="mt-3 text-[11px] text-[#6e7770] italic">
            Rate card (per page): B&amp;W/One ₹0.50 · B&amp;W/Both ₹1.00 · Color/One ₹10.00 · Color/Both ₹20.00.
            Row cost = rate × pages × copies.
          </div>
        </Section>

        <div className="mt-6 pt-4 border-t border-[#e2d7c3]">
          <label className="flex items-start gap-3 cursor-pointer select-none"
                 data-testid="print-job-consent-label">
            <input type="checkbox"
                   checked={!!form.consent_quality_disclaimer}
                   onChange={(e) => setField("consent_quality_disclaimer", e.target.checked)}
                   className="mt-1 w-5 h-5 accent-[#7a1f2b] cursor-pointer"
                   data-testid="print-job-consent-checkbox" />
            <span className="text-sm text-[#1f2320] leading-relaxed">
              <span className="font-semibold text-[#7a1f2b]">Consent (required):</span>
              {" "}Print Team is not responsible for Quality Check, Formatting or any editing before printing.
            </span>
          </label>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button className="btn-vgws btn-teal"
                  onClick={submit}
                  disabled={submitting || overLimit || !form.consent_quality_disclaimer || (poHealth && !poHealth.ok)}
                  data-testid="print-job-submit">
            <Send size={16} /> {submitting ? "Submitting…" : "Submit for approval"}
          </button>
        </div>
      </div>
    </Layout>
  );
}
